import React, { type ComponentProps, useMemo } from 'react'

import { useMemoizedObject } from '../../util/useMemoizedObject'
import type { ProductTheme, ThemeColorToken } from '../themes'
import {
  _InternalThemeProvider,
  getInitialSemanticColorVars,
  useTheme,
  useThemeElevation,
} from './ThemeProvider'

type Props = {
  children: ComponentProps<typeof _InternalThemeProvider>['children']
} & {
  [K in keyof ProductTheme['semanticColors']]?: ThemeColorToken
}

/**
 * This component causes all its descendants to use the specified new values for the
 * specified semantic colors.
 */
export function SemanticColorOverride({ children, ...overrides }: Props) {
  const prevTheme = useTheme()
  const prevThemeElevation = useThemeElevation()

  const memoizedOverrides = useMemoizedObject(overrides)
  const nextTheme = useMemo(
    () => ({
      ...prevTheme,
      semanticColors: {
        ...prevTheme.semanticColors,
        ...memoizedOverrides,
      },
    }),
    [memoizedOverrides, prevTheme]
  )
  const variables = useMemo(
    () => getInitialSemanticColorVars(nextTheme, prevThemeElevation),
    [nextTheme, prevThemeElevation]
  )

  return (
    <_InternalThemeProvider theme={nextTheme} variables={variables}>
      {children}
    </_InternalThemeProvider>
  )
}
